import $ from 'jquery';
import * as PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';

import {Optional} from 'util/Types';

export enum TooltipPlacement {
  TOP = 'top',
  AUTO = 'auto',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
}

export interface TooltipProps {
  children: React.ReactNode;
  disabled?: boolean;
  open?: boolean;

  container?: string | boolean;
  // See https://getbootstrap.com/docs/4.0/components/tooltips/
  delay?: Optional<number | object>;
  placement?: TooltipPlacement;
  title: Optional<string | number>;
  trigger?: Optional<string>;
}

/**
 * @deprecated Please use client/ui-library/floating-ui/Tooltip instead.
 */
export default class Tooltip extends React.Component<TooltipProps> {
  _hasUsedTooltipAPI: boolean = false;

  static propTypes = {
    children: PropTypes.node,
    disabled: PropTypes.bool.isRequired,
    open: PropTypes.bool,

    container: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
    delay: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    placement: PropTypes.string.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trigger: PropTypes.string,
  };

  static defaultProps = {
    container: 'body',
    placement: 'top',
    disabled: false,
  };

  _tooltipOptionsFromProps(props: TooltipProps): TooltipOptions {
    return {
      title: String(props.title),
      delay: props.delay || undefined,
      placement: props.placement,
      container: props.container,
      trigger: props.trigger || undefined,
    };
  }

  override componentDidMount(): void {
    if (!this.props.disabled) {
      this.$tooltip(this._tooltipOptionsFromProps(this.props));
    }
  }

  $tooltip(options?: TooltipOptions | string): void {
    const $this = $(ReactDOM.findDOMNode(this)!);
    // See: https://github.com/microsoft/TypeScript/issues/14107
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    $this.tooltip(options as any);
    this._hasUsedTooltipAPI = true;
  }

  override componentDidUpdate(prevProps: TooltipProps): void {
    const prevOptions = this._tooltipOptionsFromProps(prevProps);
    const newOptions = this._tooltipOptionsFromProps(this.props);
    const controlled = prevProps.open != null || this.props.open != null;
    if (isEqual(prevOptions, newOptions) && prevProps.disabled === this.props.disabled) {
      if (controlled) {
        if (this.props.open && !prevProps.open) {
          this.$tooltip('show');
        } else if (!this.props.open && prevProps.open) {
          this.$tooltip('hide');
        }
      }
      return;
    }
    if (!isEqual(prevOptions, newOptions) || prevProps.disabled !== this.props.disabled) {
      this.$tooltip('destroy');
      if (!this.props.disabled) {
        this.$tooltip(newOptions);
      }
    }
    if (controlled && !prevProps.open && this.props.open) {
      this.$tooltip('show');
    }
  }

  override componentWillUnmount(): void {
    // tooltip('destroy') will render a tooltip DOM element if it had never been rendered before cleaning up.
    // As an optimization for components that render with <Tooltip disabled={true} /> and have never rendered
    // a tooltip, simply no-op.
    if (this._hasUsedTooltipAPI) {
      this.$tooltip('destroy');
    }
  }

  override render(): React.ReactNode {
    return this.props.children;
  }
}

interface TooltipWrapperProps {
  tooltipPlacement?: TooltipPlacement;
  tooltipTitle?: string | null;
  // See https://getbootstrap.com/docs/4.0/components/tooltips/
  tooltipDelay?: Optional<number | object>;
  children: React.ReactNode;
}

export class TooltipWrapper extends React.Component<TooltipWrapperProps> {
  override render(): React.ReactNode {
    if (this.props.tooltipTitle) {
      return (
        <Tooltip
          title={this.props.tooltipTitle}
          placement={this.props.tooltipPlacement}
          delay={this.props.tooltipDelay}
        >
          {this.props.children}
        </Tooltip>
      );
    } else {
      return this.props.children;
    }
  }
}
